import { useState, type ReactNode } from 'react';

import { Button } from '@flintmint/web-shared/components/button';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { CollapsibleSection } from '@/components/collapsible-section';
import { usePageTitle } from '@/hooks/use-page-title';
import { useFlows } from '@/hooks/use-flows';
import { useMedicalModules } from '@/hooks/use-medical-modules';
import { createFlow, updateFlow, deleteFlow, type FlowInput } from '@/services/flows-service';
import { FlowRow } from './components/flow-row/flow-row';
import { AddFlowRow } from './components/add-flow-row/add-flow-row';

import styles from './flow-dashboard.module.scss';

export const FlowDashboard = () => {
  usePageTitle('Flows');
  const { flows, isLoading: flowsLoading, error: flowsError } = useFlows();
  const { modules, isLoading: modulesLoading, error: modulesError } = useMedicalModules(['complaint', 'procedure', 'other']);
  const isLoading = flowsLoading || modulesLoading;
  const [isAdding, setIsAdding] = useState(false);
  const userEmail = getAuthUser().email ?? '';

  const moduleCountByFlowId = new Map<string, number>();
  for (const module of modules) {
    for (const flowId of module.flowIds ?? []) {
      moduleCountByFlowId.set(flowId, (moduleCountByFlowId.get(flowId) ?? 0) + 1);
    }
  }

  const handleAdd = async (data: FlowInput) => {
    await createFlow(data);
    setIsAdding(false);
  };

  const handleSave = async (id: string, data: Partial<FlowInput>) => {
    await updateFlow(id, data, userEmail);
  };

  const renderContent = () => {
    if (isLoading) {
      return <FlowRowsSkeleton />;
    }

    if (flowsError || modulesError) {
      return (
        <>
          {flowsError && <p className={styles.error}>{flowsError}</p>}
          {modulesError && <p className={styles.error}>{modulesError}</p>}
        </>
      );
    }

    return (
      <>
        {isAdding && <AddFlowRow onAdd={handleAdd} onCancel={() => setIsAdding(false)} />}

        {flows.map((flow) => (
          <FlowRow
            key={flow.id}
            flow={flow}
            moduleCount={moduleCountByFlowId.get(flow.id) ?? 0}
            onSave={handleSave}
            onDelete={deleteFlow}
          />
        ))}

        {flows.length === 0 && !isAdding && <div className={styles.emptyState}>No flows</div>}
      </>
    );
  };

  return (
    <>
      <div className={styles.dashboard}>
        <FlowsSection count={flows.length} isAdding={isAdding} onAddClick={() => setIsAdding(true)}>
          {renderContent()}
        </FlowsSection>
      </div>
    </>
  );
};

export const FlowDashboardSkeleton = () => (
  <div className={styles.dashboard}>
    <FlowsSection count={0} isAdding={false} onAddClick={() => {}}>
      <FlowRowsSkeleton />
    </FlowsSection>
  </div>
);

const FlowsSection = ({
  count,
  isAdding,
  onAddClick,
  children,
}: {
  count: number;
  isAdding: boolean;
  onAddClick: () => void;
  children: ReactNode;
}) => {
  const title = (
    <>
      Flows <span className={styles.count}>({count})</span>
    </>
  );

  const actions = (
    <Button color="dark" appearance="outline" onClick={onAddClick} disabled={isAdding}>
      + Add
    </Button>
  );

  const contentHeader = (
    <div className={styles.tableHeader}>
      <span>Name</span>
      <span>Criteria</span>
      <span>Module Selection Prompt</span>
      <span>Core Prompt</span>
      <span>Modules</span>
      <span>Enabled</span>
      <span>Updated</span>
      <span>Actions</span>
    </div>
  );

  return (
    <CollapsibleSection title={title} actions={actions} contentHeader={contentHeader} stickyChromeTone="subtle">
      <div className={styles.tableBody}>{children}</div>
    </CollapsibleSection>
  );
};

const FlowRowsSkeleton = () => (
  <>
    {[0, 1].map((i) => (
      <div key={i} className={styles.row} data-testid="flow-row-skeleton">
        <Skeleton width={110} height={24} />
        <Skeleton width={220} height={24} />
        <Skeleton width={130} height={24} />
        <Skeleton width={130} height={24} />
        <Skeleton width={28} height={24} />
        <Skeleton width={40} height={24} />
        <Skeleton width={120} height={24} />
        <Skeleton width={120} height={24} />
      </div>
    ))}
  </>
);
